// AutoGRNSelect.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ChevronDown, ChevronUp, HelpCircle, RefreshCw, Save, ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Auto GRN Select (ASPX -> React)
 *
 * PAYROLL DB (year-wise) ✅
 * Table: tbl_admin_InventoryCustomization
 *
 * APIs:
 * GET  /api/auto-grn-select/list?type=GRN
 * POST /api/auto-grn-select/submit   body: { type, rows:[{id,isActive}] }
 *
 * NOTE:
 * - Payroll resolver needs finYearId, so we send header: x-finyearid
 * - Default rows are mandatory; server also enforces, UI disables them.
 */
const AutoGRNSelect = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = useMemo(() => `bg-${baseColor}-${baseShade}`, [baseColor, baseShade]);
  const hoverClass = useMemo(() => `hover:bg-${baseColor}-700`, [baseColor]);

  const navigate = useNavigate();

const handleBack = () => {
  if (saving) return;
  navigate(-1);
};
  const [showForm, setShowForm] = useState(true);

  // read ?type=GRN (fallback ?id=GRN like ASPX redirect)
  const type = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return (sp.get("type") || sp.get("id") || "GRN").toString().trim();
  }, []);

  // finYearId stored at login: localStorage.setItem("finYearId", String(finYearId))
  const finYearId = useMemo(() => {
    const v = String(localStorage.getItem("finYearId") || "").trim();
    return v;
  }, []);

  const headers = useMemo(() => {
    // payroll resolver reads header x-finyearid / x-finyear-id
    return finYearId ? { "x-finyearid": finYearId } : {};
  }, [finYearId]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchList = async () => {
    try {
      if (!finYearId) {
        toast.error("finYearId missing. Please login again / select financial year.");
        return;
      }

      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/auto-grn-select/list`, {
        params: { type },
        headers,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load steps");
        return;
      }

      const list = (res.data.list || []).map((r) => ({
        ...r,
        _isActive: !!r.fld_isactive,
        _isDefault: Number(r.fld_isdefault || 0) === 1 || String(r.fld_status).toLowerCase() === "default",
      }));

      setRows(list);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Failed to load steps");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRow = (idx, checked) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        if (r._isDefault) return r; // mandatory
        return { ...r, _isActive: checked };
      })
    );
  };

  const toggleAll = (checked) => {
    setRows((prev) =>
      prev.map((r) => (r._isDefault ? { ...r, _isActive: true } : { ...r, _isActive: checked }))
    );
  };

  const validate = () => {
    if (!finYearId) {
      toast.error("finYearId missing. Please login again / select financial year.");
      return false;
    }
    if (!type) {
      toast.error("Type missing in URL. Use ?type=GRN");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      const payload = {
        type,
        rows: rows.map((r) => ({
          id: Number(r.fld_InvCustid),
          isActive: !!r._isActive,
        })),
      };

      const res = await axios.post(`${BASE_URL}/api/auto-grn-select/submit`, payload, { headers });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Save failed");
        return;
      }

      toast.success(res.data?.message || "Saved successfully");
      await fetchList();
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  const allNonDefaultChecked = useMemo(() => {
    const nonDefault = rows.filter((r) => !r._isDefault);
    if (nonDefault.length === 0) return true;
    return nonDefault.every((r) => !!r._isActive);
  }, [rows]);

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">Stock Addition Steps / GRN Steps</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header row */}
          <div className="flex justify-between items-center gap-3 flex-wrap mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              <button
      type="button"
      onClick={handleBack}
      disabled={saving}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
        isDark
          ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
          : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
      } disabled:opacity-60`}
    >
      <ChevronLeft className="w-5 h-5" />
      Back
    </button>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Auto GRN Setup
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                Type: {type}
              </span>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-emerald-900 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                FinYearId: {finYearId || "missing"}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => window.open("/admin/help/auto-grn", "_blank")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                }`}
              >
                <HelpCircle className="w-5 h-5" />
                Help
              </button>

              <button
                type="button"
                onClick={() => {
                  fetchList();
                  toast.info("Refreshed");
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
                }`}
              >
                <RefreshCw className="w-5 h-5" />
                Refresh
              </button>

              <button
                onClick={() => setShowForm(!showForm)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                {showForm ? "Hide List" : "Show List"}
              </button>
            </div>
          </div>

          {/* Grid */}
          <div
            className={`rounded-xl border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {showForm && (
              <div className={`${isDark ? "bg-gray-800" : "bg-gray-50"} p-6`}>
                {loading ? (
                  <div className="py-10 text-center opacity-70">Loading...</div>
                ) : rows.length === 0 ? (
                  <div className="py-10 text-center opacity-70">No Records Found</div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="min-w-[900px] w-full">
                        <thead className={`${buttonClass} text-white sticky top-0 z-10`}>
                          <tr>
                            <th className="px-3 py-3 text-center font-semibold border-r border-white/20 whitespace-nowrap">
                              S.No
                            </th>
                            <th className="px-3 py-3 text-left font-semibold border-r border-white/20 whitespace-nowrap">
                              Steps Name
                            </th>
                            <th className="px-3 py-3 text-left font-semibold border-r border-white/20 whitespace-nowrap">
                              Status
                            </th>
                            <th className="px-3 py-3 text-center font-semibold whitespace-nowrap">
                              <div className="flex items-center justify-center gap-3">
                                <span>Select</span>
                                <label className="inline-flex items-center gap-2 text-sm font-semibold">
                                  <input
                                    type="checkbox"
                                    checked={allNonDefaultChecked}
                                    onChange={(e) => toggleAll(e.target.checked)}
                                    className={`w-4 h-4 cursor-pointer ${
                                      isDark ? "accent-blue-400" : "accent-blue-600"
                                    }`}
                                  />
                                  <span className="hidden md:inline">Select All</span>
                                </label>
                              </div>
                            </th>
                          </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {rows.map((r, idx) => (
                            <tr
                              key={r.fld_InvCustid}
                              className={`transition-colors duration-200 ${
                                idx % 2 === 0
                                  ? isDark
                                    ? "bg-gray-800"
                                    : "bg-white"
                                  : isDark
                                  ? "bg-gray-900"
                                  : "bg-gray-50"
                              }`}
                            >
                              <td className="px-3 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700">
                                {r.rowno ?? idx + 1}
                              </td>

                              <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700">
                                {r.fld_stepsname}
                              </td>

                              <td className="px-3 py-3 text-sm border-r border-gray-200 dark:border-gray-700">
                                {r.fld_status}
                                {r._isDefault ? (
                                  <span
                                    className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
                                      isDark
                                        ? "bg-amber-900/40 text-amber-200 ring-1 ring-amber-500/30"
                                        : "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                                    }`}
                                  >
                                    Mandatory
                                  </span>
                                ) : null}
                              </td>

                              <td className="px-3 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={!!r._isActive}
                                  disabled={saving || r._isDefault}
                                  onChange={(e) => toggleRow(idx, e.target.checked)}
                                  className={`w-4 h-4 cursor-pointer ${
                                    isDark ? "accent-blue-400" : "accent-blue-600"
                                  } ${r._isDefault ? "opacity-60 cursor-not-allowed" : ""}`}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Footer button */}
                    <div className="flex justify-center mt-5">
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving}
                        className={`flex items-center gap-2 px-8 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                      >
                        <Save className="w-5 h-5" />
                        {saving ? "Saving..." : "Submit"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default AutoGRNSelect;
